import { defineRouting } from "next-intl/routing";
import {
	DEFAULT_SITE_LOCALE,
	LIVE_SITE_LOCALES,
	type LiveSiteLocale,
} from "./locales";
import type { SitePathname } from "@/site/pages";

type LocalizedPathname = Record<LiveSiteLocale, string>;

function localizedPathname(pathnames: LocalizedPathname): LocalizedPathname {
	return pathnames;
}

// Internal pathnames stay stable in English. Public pathnames are supplied for
// every live locale. When a new locale is made live, missing slugs fail type
// checking instead of silently falling back to English.
export const LOCALIZED_PATHNAMES = {
	"/": localizedPathname({
		en: "/",
		zh: "/",
		es: "/",
		pt: "/",
		de: "/",
		ja: "/",
		fr: "/",
		ko: "/",
		ar: "/",
		id: "/",
		it: "/",
		"zh-hant": "/",
		ru: "/",
		tr: "/",
		pl: "/",
		nl: "/",
		vi: "/",
		th: "/",
		fil: "/",
		ms: "/",
		he: "/",
		uk: "/",
		sv: "/",
		no: "/",
		da: "/",
		fi: "/",
		ro: "/",
		cs: "/",
		hu: "/",
		el: "/",
		bg: "/",
		hr: "/",
		sk: "/",
		sl: "/",
		ca: "/",
		lt: "/",
		lv: "/",
		et: "/",
		is: "/",
		hi: "/",
	}),
	"/about": localizedPathname({
		en: "/about",
		zh: "/关于",
		es: "/acerca-de",
		pt: "/sobre",
		de: "/über-uns",
		ja: "/このサイトについて",
		fr: "/a-propos",
		ko: "/소개",
		ar: "/من-نحن",
		id: "/tentang",
		it: "/chi-siamo",
		"zh-hant": "/關於",
		ru: "/о-сервисе",
		tr: "/hakkımızda",
		pl: "/o-nas",
		nl: "/over-ons",
		vi: "/gioi-thieu",
		th: "/เกี่ยวกับ",
		fil: "/tungkol",
		ms: "/tentang",
		he: "/אודות",
		uk: "/про-нас",
		sv: "/om-oss",
		no: "/om-oss",
		da: "/om-os",
		fi: "/tietoa-meistä",
		ro: "/despre-noi",
		cs: "/o-nas",
		hu: "/rolunk",
		el: "/σχετικά",
		bg: "/за-нас",
		hr: "/o-nama",
		sk: "/o-nas",
		sl: "/o-nas",
		ca: "/sobre-nosaltres",
		lt: "/apie-mus",
		lv: "/par-mums",
		et: "/meist",
		is: "/um-okkur",
		hi: "/हमारे-बारे-में",
	}),
	"/privacy": localizedPathname({
		en: "/privacy",
		zh: "/隐私政策",
		es: "/politica-de-privacidad",
		pt: "/politica-de-privacidade",
		de: "/datenschutz",
		ja: "/プライバシーポリシー",
		fr: "/politique-de-confidentialite",
		ko: "/개인정보처리방침",
		ar: "/سياسة-الخصوصية",
		id: "/kebijakan-privasi",
		it: "/informativa-privacy",
		"zh-hant": "/隱私權政策",
		ru: "/политика-конфиденциальности",
		tr: "/gizlilik-politikası",
		pl: "/polityka-prywatności",
		nl: "/privacybeleid",
		vi: "/chinh-sach-bao-mat",
		th: "/นโยบายความเป็นส่วนตัว",
		fil: "/patakaran-sa-privacy",
		ms: "/dasar-privasi",
		he: "/מדיניות-פרטיות",
		uk: "/політика-конфіденційності",
		sv: "/integritetspolicy",
		no: "/personvern",
		da: "/privatlivspolitik",
		fi: "/tietosuojakäytäntö",
		ro: "/politica-de-confidentialitate",
		cs: "/zasady-ochrany-osobnich-udaju",
		hu: "/adatvedelmi-iranyelvek",
		el: "/πολιτική-απορρήτου",
		bg: "/политика-за-поверителност",
		hr: "/pravila-privatnosti",
		sk: "/zasady-ochrany-osobnych-udajov",
		sl: "/pravilnik-o-zasebnosti",
		ca: "/politica-de-privacitat",
		lt: "/privatumo-politika",
		lv: "/privatuma-politika",
		et: "/privaatsuspoliitika",
		is: "/personuverndarstefna",
		hi: "/गोपनीयता-नीति",
	}),
	"/terms": localizedPathname({
		en: "/terms",
		zh: "/服务条款",
		es: "/terminos-de-servicio",
		pt: "/termos-de-uso",
		de: "/nutzungsbedingungen",
		ja: "/利用規約",
		fr: "/conditions-utilisation",
		ko: "/이용약관",
		ar: "/شروط-الاستخدام",
		id: "/ketentuan-layanan",
		it: "/termini-di-servizio",
		"zh-hant": "/服務條款",
		ru: "/условия-использования",
		tr: "/kullanım-koşulları",
		pl: "/warunki-korzystania",
		nl: "/gebruiksvoorwaarden",
		vi: "/dieu-khoan-su-dung",
		th: "/ข้อกำหนดการใช้งาน",
		fil: "/mga-tuntunin-ng-serbisyo",
		ms: "/terma-perkhidmatan",
		he: "/תנאי-שימוש",
		uk: "/умови-користування",
		sv: "/användarvillkor",
		no: "/bruksvilkår",
		da: "/brugsvilkår",
		fi: "/käyttöehdot",
		ro: "/termeni-de-utilizare",
		cs: "/podminky-pouziti",
		hu: "/felhasznalasi-feltetelek",
		el: "/όροι-χρήσης",
		bg: "/условия-за-ползване",
		hr: "/uvjeti-korištenja",
		sk: "/podmienky-pouzivania",
		sl: "/pogoji-uporabe",
		ca: "/condicions-d-us",
		lt: "/naudojimo-sąlygos",
		lv: "/lietosanas-noteikumi",
		et: "/kasutustingimused",
		is: "/notkunarskilmalar",
		hi: "/उपयोग-की-शर्तें",
	}),
	"/tools/video-converter": localizedPathname({
		en: "/tools/video-converter",
		zh: "/工具/视频转换器",
		es: "/herramientas/conversor-de-video",
		pt: "/ferramentas/conversor-de-video",
		de: "/werkzeuge/video-konverter",
		ja: "/ツール/動画変換",
		fr: "/outils/convertisseur-video",
		ko: "/도구/동영상-변환기",
		ar: "/أدوات/محول-الفيديو",
		id: "/alat/konverter-video",
		it: "/strumenti/convertitore-video",
		"zh-hant": "/工具/影片轉換器",
		ru: "/инструменты/конвертер-видео",
		tr: "/araçlar/video-dönüştürücü",
		pl: "/narzędzia/konwerter-wideo",
		nl: "/tools/videoconverter",
		vi: "/cong-cu/chuyen-doi-video",
		th: "/เครื่องมือ/แปลงวิดีโอ",
		fil: "/mga-kasangkapan/video-converter",
		ms: "/alat/penukar-video",
		he: "/כלים/ממיר-וידאו",
		uk: "/інструменти/конвертер-відео",
		sv: "/verktyg/videokonverterare",
		no: "/verktøy/videokonverter",
		da: "/værktøjer/videokonverter",
		fi: "/työkalut/videomuunnin",
		ro: "/instrumente/convertor-video",
		cs: "/nástroje/převodník-videa",
		hu: "/eszközök/videókonverter",
		el: "/εργαλεία/μετατροπέας-βίντεο",
		bg: "/инструменти/видео-конвертор",
		hr: "/alati/video-konverter",
		sk: "/nástroje/konvertor-videa",
		sl: "/orodja/video-pretvornik",
		ca: "/eines/conversor-de-video",
		lt: "/įrankiai/vaizdo-keitiklis",
		lv: "/rīki/video-pārveidotājs",
		et: "/tööriistad/videomuundur",
		is: "/verkfæri/myndbandsumbreytir",
		hi: "/उपकरण/वीडियो-कन्वर्टर",
	}),
	"/tools/video-to-gif": localizedPathname({
		en: "/tools/video-to-gif",
		zh: "/工具/视频转GIF",
		es: "/herramientas/video-a-gif",
		pt: "/ferramentas/video-para-gif",
		de: "/werkzeuge/video-in-gif-umwandeln",
		ja: "/ツール/動画をGIFに変換",
		fr: "/outils/video-en-gif",
		ko: "/도구/동영상을-GIF로-변환",
		ar: "/أدوات/تحويل-الفيديو-إلى-gif",
		id: "/alat/video-ke-gif",
		it: "/strumenti/video-in-gif",
		"zh-hant": "/工具/影片轉GIF",
		ru: "/инструменты/видео-в-gif",
		tr: "/araçlar/video-gif-dönüştürme",
		pl: "/narzędzia/wideo-do-gif",
		nl: "/tools/video-naar-gif",
		vi: "/cong-cu/video-sang-gif",
		th: "/เครื่องมือ/วิดีโอเป็น-gif",
		fil: "/mga-kasangkapan/video-sa-gif",
		ms: "/alat/video-kepada-gif",
		he: "/כלים/וידאו-ל-gif",
		uk: "/інструменти/відео-в-gif",
		sv: "/verktyg/video-till-gif",
		no: "/verktøy/video-til-gif",
		da: "/værktøjer/video-til-gif",
		fi: "/työkalut/video-gif-muotoon",
		ro: "/instrumente/video-in-gif",
		cs: "/nástroje/video-do-gif",
		hu: "/eszközök/videó-gif-be",
		el: "/εργαλεία/βίντεο-σε-gif",
		bg: "/инструменти/видео-в-gif",
		hr: "/alati/video-u-gif",
		sk: "/nástroje/video-do-gif",
		sl: "/orodja/video-v-gif",
		ca: "/eines/video-a-gif",
		lt: "/įrankiai/video-į-gif",
		lv: "/rīki/video-uz-gif",
		et: "/tööriistad/video-gif-iks",
		is: "/verkfæri/myndband-í-gif",
		hi: "/उपकरण/वीडियो-से-gif",
	}),
	"/tools/resize-video": localizedPathname({
		en: "/tools/resize-video",
		zh: "/工具/调整视频尺寸",
		es: "/herramientas/redimensionar-video",
		pt: "/ferramentas/redimensionar-video",
		de: "/werkzeuge/video-groesse-aendern",
		ja: "/ツール/動画サイズ変更",
		fr: "/outils/redimensionner-video",
		ko: "/도구/동영상-크기-조절",
		ar: "/أدوات/تغيير-حجم-الفيديو",
		id: "/alat/ubah-ukuran-video",
		it: "/strumenti/ridimensionare-video",
		"zh-hant": "/工具/調整影片尺寸",
		ru: "/инструменты/изменить-размер-видео",
		tr: "/araçlar/video-boyutlandırma",
		pl: "/narzędzia/zmiana-rozmiaru-wideo",
		nl: "/tools/video-formaat-wijzigen",
		vi: "/cong-cu/thay-doi-kich-thuoc-video",
		th: "/เครื่องมือ/ปรับขนาดวิดีโอ",
		fil: "/mga-kasangkapan/baguhin-laki-video",
		ms: "/alat/ubah-saiz-video",
		he: "/כלים/שינוי-גודל-וידאו",
		uk: "/інструменти/змінити-розмір-відео",
		sv: "/verktyg/andra-videostorlek",
		no: "/verktøy/endre-videostørrelse",
		da: "/værktøjer/ændr-videostørrelse",
		fi: "/työkalut/muuta-videon-kokoa",
		ro: "/instrumente/redimensionare-video",
		cs: "/nástroje/změna-velikosti-videa",
		hu: "/eszközök/videó-átméretezése",
		el: "/εργαλεία/αλλαγή-μεγέθους-βίντεο",
		bg: "/инструменти/преоразмеряване-на-видео",
		hr: "/alati/promjena-veličine-videa",
		sk: "/nástroje/zmena-veľkosti-videa",
		sl: "/orodja/spremeni-velikost-videa",
		ca: "/eines/redimensionar-video",
		lt: "/įrankiai/keisti-vaizdo-įrašo-dydį",
		lv: "/rīki/mainīt-video-izmēru",
		et: "/tööriistad/muuda-video-suurust",
		is: "/verkfæri/breyta-stærð-myndbands",
		hi: "/उपकरण/वीडियो-का-आकार-बदलें",
	}),
	"/tools/screen-recorder": localizedPathname({
		en: "/tools/screen-recorder",
		zh: "/工具/屏幕录制",
		es: "/herramientas/grabador-de-pantalla",
		pt: "/ferramentas/gravador-de-tela",
		de: "/werkzeuge/bildschirmrekorder",
		ja: "/ツール/画面録画",
		fr: "/outils/enregistreur-ecran",
		ko: "/도구/화면-녹화",
		ar: "/أدوات/مسجل-الشاشة",
		id: "/alat/perekam-layar",
		it: "/strumenti/registratore-schermo",
		"zh-hant": "/工具/螢幕錄影",
		ru: "/инструменты/запись-экрана",
		tr: "/araclar/ekran-kaydedici",
		pl: "/narzedzia/nagrywanie-ekranu",
		nl: "/tools/schermopname",
		vi: "/cong-cu/ghi-man-hinh",
		th: "/เครื่องมือ/บันทึกหน้าจอ",
		fil: "/mga-kagamitan/pag-record-ng-screen",
		ms: "/alat/perakam-skrin",
		he: "/כלים/מקליט-מסך",
		uk: "/інструменти/запис-екрана",
		sv: "/verktyg/skärminspelare",
		no: "/verktoy/skjermopptaker",
		da: "/vaerktojer/skaermoptager",
		fi: "/tyokalut/nayton-tallennus",
		ro: "/instrumente/inregistrare-ecran",
		cs: "/nastroje/nahravani-obrazovky",
		hu: "/eszkozok/kepernyofelvevo",
		el: "/εργαλεία/εγγραφή-οθόνης",
		bg: "/инструменти/запис-на-екрана",
		hr: "/alati/snimanje-zaslona",
		sk: "/nastroje/nahravanie-obrazovky",
		sl: "/orodja/snemalnik-zaslona",
		ca: "/eines/gravador-de-pantalla",
		lt: "/irankiai/ekrano-irasymas",
		lv: "/riki/ekrana-ierakstitajs",
		et: "/tooriistad/ekraanisalvesti",
		is: "/verkfaeri/skjaupptaka",
		hi: "/उपकरण/स्क्रीन-रिकॉर्डर",
	}),
	"/tools/crop-video": localizedPathname({
		en: "/tools/crop-video",
		zh: "/工具/裁剪视频",
		es: "/herramientas/recortar-video",
		pt: "/ferramentas/cortar-video",
		de: "/werkzeuge/video-zuschneiden",
		ja: "/ツール/動画クロップ",
		fr: "/outils/recadrer-video",
		ko: "/도구/동영상-화면-자르기",
		ar: "/أدوات/اقتصاص-الفيديو",
		id: "/alat/pangkas-video",
		it: "/strumenti/ritagliare-video",
		"zh-hant": "/工具/裁切影片",
		ru: "/инструменты/обрезать-видео",
		tr: "/araçlar/video-kırpma",
		pl: "/narzędzia/przycinanie-wideo",
		nl: "/tools/video-bijsnijden",
		vi: "/cong-cu/cat-khung-video",
		th: "/เครื่องมือ/ครอบตัดวิดีโอ",
		fil: "/mga-kasangkapan/i-crop-ang-video",
		ms: "/alat/pangkas-video",
		he: "/כלים/חיתוך-וידאו",
		uk: "/інструменти/обрізати-відео",
		sv: "/verktyg/beskär-video",
		no: "/verktøy/beskjær-video",
		da: "/værktøjer/beskær-video",
		fi: "/työkalut/rajaa-video",
		ro: "/instrumente/decupare-video",
		cs: "/nástroje/oříznout-video",
		hu: "/eszközök/videó-kivágása",
		el: "/εργαλεία/περικοπή-βίντεο",
		bg: "/инструменти/изрязване-на-видео",
		hr: "/alati/izreži-video",
		sk: "/nástroje/orezať-video",
		sl: "/orodja/obreži-video",
		ca: "/eines/retallar-video",
		lt: "/įrankiai/apkarpyti-vaizdo-įrašą",
		lv: "/rīki/apgriezt-video",
		et: "/tööriistad/kärbi-video",
		is: "/verkfæri/skera-myndband",
		hi: "/उपकरण/वीडियो-क्रॉप",
	}),
	"/tools/rotate-video": localizedPathname({
		en: "/tools/rotate-video",
		zh: "/工具/旋转视频",
		es: "/herramientas/rotar-video",
		pt: "/ferramentas/girar-video",
		de: "/werkzeuge/video-drehen",
		ja: "/ツール/動画回転",
		fr: "/outils/pivoter-video",
		ko: "/도구/동영상-회전",
		ar: "/أدوات/تدوير-الفيديو",
		id: "/alat/putar-video",
		it: "/strumenti/ruotare-video",
		"zh-hant": "/工具/旋轉影片",
		ru: "/инструменты/повернуть-видео",
		tr: "/araçlar/video-döndürme",
		pl: "/narzędzia/obracanie-wideo",
		nl: "/tools/video-draaien",
		vi: "/cong-cu/xoay-video",
		th: "/เครื่องมือ/หมุนวิดีโอ",
		fil: "/mga-kasangkapan/i-rotate-ang-video",
		ms: "/alat/putar-video",
		he: "/כלים/סיבוב-וידאו",
		uk: "/інструменти/повернути-відео",
		sv: "/verktyg/rotera-video",
		no: "/verktøy/roter-video",
		da: "/værktøjer/roter-video",
		fi: "/työkalut/kierrä-video",
		ro: "/instrumente/rotire-video",
		cs: "/nástroje/otočit-video",
		hu: "/eszközök/videó-forgatása",
		el: "/εργαλεία/περιστροφή-βίντεο",
		bg: "/инструменти/завъртане-на-видео",
		hr: "/alati/rotiraj-video",
		sk: "/nástroje/otočiť-video",
		sl: "/orodja/zavrti-video",
		ca: "/eines/girar-video",
		lt: "/įrankiai/pasukti-vaizdo-įrašą",
		lv: "/rīki/pagriezt-video",
		et: "/tööriistad/pööra-videot",
		is: "/verkfæri/snúa-myndbandi",
		hi: "/उपकरण/वीडियो-घुमाएं",
	}),
	"/tools/video-compressor": localizedPathname({
		en: "/tools/video-compressor",
		zh: "/工具/视频压缩器",
		es: "/herramientas/compresor-de-video",
		pt: "/ferramentas/compressor-de-video",
		de: "/werkzeuge/video-komprimieren",
		ja: "/ツール/動画圧縮",
		fr: "/outils/compresseur-video",
		ko: "/도구/동영상-압축",
		ar: "/أدوات/ضاغط-الفيديو",
		id: "/alat/kompres-video",
		it: "/strumenti/compressore-video",
		"zh-hant": "/工具/影片壓縮器",
		ru: "/инструменты/сжать-видео",
		tr: "/araçlar/video-sıkıştırma",
		pl: "/narzędzia/kompresor-wideo",
		nl: "/tools/videocompressor",
		vi: "/cong-cu/nen-video",
		th: "/เครื่องมือ/บีบอัดวิดีโอ",
		fil: "/mga-kasangkapan/i-compress-ang-video",
		ms: "/alat/pemampat-video",
		he: "/כלים/דחיסת-וידאו",
		uk: "/інструменти/стиснути-відео",
		sv: "/verktyg/videokomprimering",
		no: "/verktøy/videokomprimering",
		da: "/værktøjer/videokomprimering",
		fi: "/työkalut/videon-pakkaus",
		ro: "/instrumente/compresor-video",
		cs: "/nástroje/kompresor-videa",
		hu: "/eszközök/videótömörítő",
		el: "/εργαλεία/συμπιεστής-βίντεο",
		bg: "/инструменти/компресор-за-видео",
		hr: "/alati/kompresor-videa",
		sk: "/nástroje/kompresor-videa",
		sl: "/orodja/stiskanje-videa",
		ca: "/eines/compressor-de-video",
		lt: "/įrankiai/vaizdo-kompresorius",
		lv: "/rīki/video-saspiešana",
		et: "/tööriistad/video-tihendamine",
		is: "/verkfæri/myndbandsþjöppun",
		hi: "/उपकरण/वीडियो-कंप्रेसर",
	}),
	"/projects": localizedPathname({
		en: "/projects",
		zh: "/项目",
		es: "/proyectos",
		pt: "/projetos",
		de: "/projekte",
		ja: "/プロジェクト",
		fr: "/projets",
		ko: "/프로젝트",
		ar: "/المشاريع",
		id: "/proyek",
		it: "/progetti",
		"zh-hant": "/專案",
		ru: "/проекты",
		tr: "/projeler",
		pl: "/projekty",
		nl: "/projecten",
		vi: "/du-an",
		th: "/โปรเจกต์",
		fil: "/mga-proyekto",
		ms: "/projek",
		he: "/פרויקטים",
		uk: "/проєкти",
		sv: "/projekt",
		no: "/prosjekter",
		da: "/projekter",
		fi: "/projektit",
		ro: "/proiecte",
		cs: "/projekty",
		hu: "/projektek",
		el: "/έργα",
		bg: "/проекти",
		hr: "/projekti",
		sk: "/projekty",
		sl: "/projekti",
		ca: "/projectes",
		lt: "/projektai",
		lv: "/projekti",
		et: "/projektid",
		is: "/verkefni",
		hi: "/प्रोजेक्ट",
	}),
	"/editor/[project_id]": localizedPathname({
		en: "/editor/[project_id]",
		zh: "/剪辑/[project_id]",
		es: "/editar/[project_id]",
		pt: "/editar/[project_id]",
		de: "/bearbeiten/[project_id]",
		ja: "/編集/[project_id]",
		fr: "/editer/[project_id]",
		ko: "/편집/[project_id]",
		ar: "/تحرير/[project_id]",
		id: "/edit/[project_id]",
		it: "/modifica/[project_id]",
		"zh-hant": "/剪輯/[project_id]",
		ru: "/редактор/[project_id]",
		tr: "/düzenle/[project_id]",
		pl: "/edytor/[project_id]",
		nl: "/editor/[project_id]",
		vi: "/chinh-sua/[project_id]",
		th: "/ตัดต่อ/[project_id]",
		fil: "/mag-edit/[project_id]",
		ms: "/sunting/[project_id]",
		he: "/עריכה/[project_id]",
		uk: "/редактор/[project_id]",
		sv: "/redigera/[project_id]",
		no: "/rediger/[project_id]",
		da: "/rediger/[project_id]",
		fi: "/muokkaa/[project_id]",
		ro: "/editeaza/[project_id]",
		cs: "/upravit/[project_id]",
		hu: "/szerkesztes/[project_id]",
		el: "/επεξεργασία/[project_id]",
		bg: "/редактиране/[project_id]",
		hr: "/uredi/[project_id]",
		sk: "/upravit/[project_id]",
		sl: "/uredi/[project_id]",
		ca: "/edita/[project_id]",
		lt: "/redaguoti/[project_id]",
		lv: "/rediget/[project_id]",
		et: "/redigeeri/[project_id]",
		is: "/breyta/[project_id]",
		hi: "/एडिट/[project_id]",
	}),
} satisfies Record<SitePathname, LocalizedPathname | string>;

export const routing = defineRouting({
	locales: LIVE_SITE_LOCALES,
	defaultLocale: DEFAULT_SITE_LOCALE,
	localePrefix: "as-needed",
	localeDetection: false,
	localeCookie: false,
	// Metadata and sitemap generation are page-aware and are the single source
	// of truth for alternate links.
	alternateLinks: false,
	pathnames: LOCALIZED_PATHNAMES,
});
